import { apiClient, apiPdfClient, v3Client } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { CommentViewModel } from '@store/orders';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import { checkFormatAndSetDate } from '@views/invoicing/utils';
import { DateTime } from 'luxon';

import {
  InvoicingOrder,
  MailModalFormTypes,
  PaymentDetail,
  ProcessInvoicingPayload,
} from './types';

const isPortRemainingV3ApisEnabled = isFeatureFlagEnabled(
  FeatureFlags.PortRemainingV3Apis,
);

export const fetchOrderForAccountingInfo = async (orderId: number) => {
  try {
    const response: { value: InvoicingOrder } = await apiClient.get(
      `Accounting/getcustomerdata/${orderId}`,
    );
    return response.value;
  } catch (error) {
    const err = createError(
      error,
      'Failed to fetch the accounting info for the order',
    );
    handleToast(ToastType.Error, err.message);
  }
};

export const getCustomerInvoiceApi = async (
  orderId: number,
  urls: string[],
) => {
  try {
    const resp = await apiPdfClient.post(
      `/pdf/generatecustomerinvoice/${orderId}`,
      {
        urls,
      },
    );
    return resp as unknown as {
      isSuccess: boolean;
      value: string;
    };
  } catch (error) {
    const err = createError(error, 'Failed to generate customer invoice');
    handleToast(ToastType.Error, err.message);
  }
};
export const sendEmailRequest = async (
  orderId: number,
  params: MailModalFormTypes,
) => {
  try {
    if (isPortRemainingV3ApisEnabled()) {
      const response: V4ApiResponse<boolean> = await apiClient.post(
        'accounting/emailcustomerinvoice',
        {
          ...params,
        },
      );

      return {
        success: response.isSuccess,
      };
    }

    const response = await v3Client.post(
      `Accounting/emailcustomerinvoice/${orderId}`,
      {
        ...params,
      },
    );
    return response as unknown as {
      success: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to email Customer invoice');
    handleToast(ToastType.Error, err.message);
  }
};

export const sendMailRequest = async (orderId: number, invoiceUrl: string) => {
  try {
    const response = await apiClient.get(
      `Accounting/MailCustomerInvoice?orderId=${orderId}&invoiceUrl=${encodeURIComponent(
        invoiceUrl,
      )}`,
    );
    return response as unknown as {
      isSuccess: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to send the mail');
    handleToast(ToastType.Error, err.message);
  }
};

export const updateDropOffDatesRequest = async (payload: unknown) => {
  try {
    const response = await apiClient.put('Load/UpdateDropOffDates', payload);
    return response as unknown as {
      isSuccess: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to update drop off dates');
    handleToast(ToastType.Error, err.message);
  }
};

export const setOrderInvoicedDateRequest = async (
  orderId: number,
  date: string,
) => {
  try {
    const response = await apiClient.post(
      `accounting/setorderinvoiceddate?orderId=${orderId}&date=${date}`,
    );
    return response as unknown as {
      isSuccess: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to set order invoiced date');
    handleToast(ToastType.Error, err.message);
  }
};

export const setVendorBillingInfoRequest = async (
  payload: Record<string, unknown>,
) => {
  try {
    const response = await apiClient.post(
      'order/SetVendorBillingInfo',
      payload,
    );
    return response as unknown as {
      isSuccess: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to set vendor billing info');
    handleToast(ToastType.Error, err.message);
  }
};

export const setCarrierUpdateAddressRequest = async (
  payload: Record<string, unknown>,
) => {
  try {
    if (isPortRemainingV3ApisEnabled()) {
      const response: V4ApiResponse<unknown> = await apiClient.post(
        `Carrier/UpdateAddress/${payload.id}`,
        payload,
      );
      return { success: response.isSuccess };
    }

    //NOTE: THIS API HAS Some problems with apiclient, so using v3Client
    const response = await v3Client.post(
      `Carrier/UpdateAddress/${payload.id}`,
      payload,
    );
    return response as unknown as {
      success: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to set carrier update address');
    handleToast(ToastType.Error, err.message);
  }
};

export const setFactoringCompanyRequest = async (
  factorId: number,
  carrierId: number,
) => {
  try {
    const response = await apiClient.post(
      `carrier/setfactoringcompany?factorId=${factorId}&carrierId=${carrierId}`,
    );
    return response as unknown as {
      isSuccess: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to set factoring company');
    handleToast(ToastType.Error, err.message);
  }
};

export const setCarrierBilledDetailsRequest = async (payload: {
  billRef: string | null;
  loadId: number;
  quickPay: boolean;
  billedDate: string | null;
  expectedPaidDate: string | null;
}) => {
  try {
    const { billRef, loadId, quickPay, billedDate, expectedPaidDate } = payload;
    const formattedBilledDate = Boolean(billedDate)
      ? DateTime.fromISO(checkFormatAndSetDate(billedDate as string)).toFormat(
          'M/d/yyyy',
        )
      : '';
    const formattedExpectedPaidDate = Boolean(expectedPaidDate)
      ? DateTime.fromISO(
          checkFormatAndSetDate(expectedPaidDate as string),
        ).toFormat('M/d/yyyy')
      : '';
    const response = await apiClient.post(
      `accounting/setcarrierbilleddate?loadId=${loadId.toString()}&billedDate=${formattedBilledDate}&expectedPaidDate=${formattedExpectedPaidDate}&billRef=${
        Boolean(billRef) ? payload.billRef : ' '
      }&quickpay=${Boolean(quickPay)}`,
    );
    return response as unknown as {
      isSuccess: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to set carrier billed date');
    handleToast(ToastType.Error, err.message);
  }
};

export const voidCheckApi = async (lineItemId: number, txnId: string) => {
  try {
    const response: V4ApiResponse<unknown> = await apiClient.post(
      `Load/LineItem/${lineItemId}/VoidCheck`,
      {
        paymentId: lineItemId,
        txnId,
      },
    );
    return response;
  } catch (error) {
    const err = createError(error, 'Failed to void check');
    handleToast(ToastType.Error, err.message);
  }
};

export const checkLoadDetailsApi = async (moneyCode: string) => {
  try {
    const response: V4ApiResponse<PaymentDetail> = await apiClient.get(
      'Load/CheckDetails',
      {
        params: {
          code: moneyCode,
        },
      },
    );
    if (!response.isSuccess) {
      handleToast(
        ToastType.Error,
        response.errors[0]?.message ?? 'Failed to check load details',
      );
      return;
    }
    return response.value;
  } catch (error) {
    const err = createError(error, 'Failed to check load details');
    handleToast(ToastType.Error, err.message);
  }
};

export const processInvoicingOrderApi = async (
  payload: ProcessInvoicingPayload,
) => {
  try {
    const resp: {
      value: {
        success: boolean;
        partialSuccess: boolean;
        isOrderSuccessful: boolean;
        isLoadSuccessful: boolean;
        isVendorSuccessful: boolean;
        errors: [
          {
            code: string;
            message: string;
          },
        ];
      };
      errors: [
        {
          code: string;
          message: string;
        },
      ];
    } = await apiClient.post('Accounting/processorder', payload);
    return resp.value;
  } catch (error) {
    const err = createError(error, 'Failed to process order');
    handleToast(ToastType.Error, err.message);
  }
};

export const generateEdiInvoiceApi = async (
  orderId: number,
  documents: string[],
) => {
  try {
    const resp = await v3Client.post(`EDI/Invoice/${orderId}`, {
      documents: documents,
      orderId,
    });
    return resp as unknown as {
      success: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to generate EDI invoice');
    handleToast(ToastType.Error, err.message);
  }
};

export const syncInvoiceApi = async (invoiceId: string) => {
  try {
    //check
    const resp = await apiClient.post('/Accounting/SyncInvoicePayment', {
      invoiceId,
    });
    return resp as unknown as {
      isSuccess: boolean;
      value: { invoiceId: string; success: boolean }[];
    };
  } catch (error) {
    const err = createError(error, `Error syncing invoice: ${invoiceId}`);
    handleToast(ToastType.Error, err.message);
  }
};

export const reprocessDocumentsApi = async (
  loadNumber: string,
  quickPay: boolean,
) => {
  try {
    if (isPortRemainingV3ApisEnabled()) {
      const response: V4ApiResponse<unknown> = await apiClient.get(
        'LoadDocument/AddToBillingQueue/',
        { params: { loadNumber, quickPay } },
      );
      return { success: response.isSuccess };
    }

    const resp = await v3Client.get(
      `LoadDocument/AddToBillingQueue/?loadNumber=${loadNumber}&quickPay=${quickPay}`,
    );
    return resp as unknown as {
      success: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to reprocess documents');
    handleToast(ToastType.Error, err.message);
  }
};

export const deleteInvoicingDocumentApi = async (fileName: string) => {
  try {
    const file =
      fileName.split('?url=').length > 1
        ? fileName.split('?url=')[1]
        : fileName;
    const resp = await apiClient.post('Document/DeleteFile', {
      file,
    });
    return resp as unknown as {
      isSuccess: boolean;
    };
  } catch (error) {
    const err = createError(error, 'Failed to delete file');
    handleToast(ToastType.Error, err.message);
  }
};

export const addApprovalFlagCommentApi = async (
  orderId: number,
  comment: string,
  flagId?: number,
) => {
  try {
    const params: {
      orderId: number;
      comment: string;
      flagId?: number;
    } = {
      orderId,
      comment,
    };
    params.flagId = flagId;
    const resp = await apiClient.post('/Accounting/AddApprovalComment', {
      ...params,
    });
    return resp as unknown as {
      isSuccess: boolean;
      value: {
        flagViewModel: FlagViewModel | null;
        commentViewModel: CommentViewModel | null;
      };
    };
  } catch (error) {
    const err = createError(error, 'Failed to add approval flag comment');
    handleToast(ToastType.Error, err.message);
  }
};
